import { useState, useRef, type ChangeEvent } from 'react'
import { Camera, KeyRound, User } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { FloatingInput } from '@/components/FloatingInput'

function Section({ icon: Icon, title, children }: {
  icon: typeof User
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center gap-2 mb-5">
        <Icon size={16} className="text-blue-500" />
        <h2 className="text-sm font-bold text-gray-900">{title}</h2>
      </div>
      {children}
    </div>
  )
}

export default function Settings() {
  const { user } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)

  const [nama, setNama] = useState(user?.user_metadata?.nama ?? '')
  const [namaLoading, setNamaLoading] = useState(false)
  const [namaMsg, setNamaMsg] = useState('')

  const [avatarUrl, setAvatarUrl] = useState<string>(user?.user_metadata?.avatar_url ?? '')
  const [avatarLoading, setAvatarLoading] = useState(false)

  const [passwords, setPasswords] = useState({ baru: '', konfirmasi: '' })
  const [passLoading, setPassLoading] = useState(false)
  const [passMsg, setPassMsg] = useState('')
  const [passError, setPassError] = useState('')

  async function handleSaveNama() {
    if (!nama.trim()) return
    setNamaLoading(true)
    setNamaMsg('')
    const { error } = await supabase.auth.updateUser({ data: { nama } })
    if (!error) {
      await supabase.from('pelanggan').update({ nama }).eq('id', user!.id)
      setNamaMsg('Nama berhasil diperbarui.')
    }
    setNamaLoading(false)
  }

  async function handleAvatarChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !user) return
    setAvatarLoading(true)

    const ext = file.name.split('.').pop()
    const path = `${user.id}.${ext}`

    // NOTE: buat bucket "avatars" di Supabase Storage (public) sebelum ini berjalan
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true })

    if (!uploadError) {
      const { data } = supabase.storage.from('avatars').getPublicUrl(path)
      const url = data.publicUrl
      await supabase.auth.updateUser({ data: { avatar_url: url } })
      setAvatarUrl(url)
    }
    setAvatarLoading(false)
  }

  async function handleSavePassword() {
    setPassError('')
    setPassMsg('')
    if (passwords.baru.length < 6) {
      setPassError('Password minimal 6 karakter.')
      return
    }
    if (passwords.baru !== passwords.konfirmasi) {
      setPassError('Konfirmasi password tidak cocok.')
      return
    }
    setPassLoading(true)
    const { error } = await supabase.auth.updateUser({ password: passwords.baru })
    setPassLoading(false)
    if (error) {
      setPassError(error.message)
    } else {
      setPassMsg('Password berhasil diperbarui.')
      setPasswords({ baru: '', konfirmasi: '' })
    }
  }

  if (!user) return null

  return (
    <div className="max-w-xl mx-auto px-4 py-8 pb-12 flex flex-col gap-5">
      <h1 className="text-xl font-bold text-gray-900">Pengaturan Akun</h1>

      {/* Foto Profil */}
      <Section icon={Camera} title="Foto Profil">
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-bold text-blue-600">
                {nama.charAt(0).toUpperCase()}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={avatarLoading}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-colors"
            >
              <Camera size={12} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700">{nama}</p>
            <p className="text-xs text-gray-400 mt-0.5">{user.email}</p>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={avatarLoading}
              className="mt-2 text-xs text-blue-600 hover:underline"
            >
              {avatarLoading ? 'Mengunggah...' : 'Ganti foto'}
            </button>
          </div>
        </div>
      </Section>

      {/* Display Name */}
      <Section icon={User} title="Nama Tampilan">
        <div className="flex flex-col gap-3">
          <FloatingInput
            label="Nama Lengkap"
            value={nama}
            onChange={e => { setNama(e.target.value); setNamaMsg('') }}
          />
          <div className="flex items-center justify-between">
            {namaMsg && <p className="text-xs text-green-600">{namaMsg}</p>}
            <div className="ml-auto">
              <button
                onClick={handleSaveNama}
                disabled={!nama.trim() || namaLoading}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  !nama.trim() || namaLoading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {namaLoading ? 'Menyimpan...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Password */}
      <Section icon={KeyRound} title="Ganti Password">
        <div className="flex flex-col gap-3">
          <FloatingInput
            label="Password Baru"
            type="password"
            value={passwords.baru}
            onChange={e => { setPasswords(p => ({ ...p, baru: e.target.value })); setPassError(''); setPassMsg('') }}
          />
          <FloatingInput
            label="Konfirmasi Password"
            type="password"
            value={passwords.konfirmasi}
            onChange={e => { setPasswords(p => ({ ...p, konfirmasi: e.target.value })); setPassError(''); setPassMsg('') }}
          />
          {passError && <p className="text-xs text-red-500">{passError}</p>}
          {passMsg && <p className="text-xs text-green-600">{passMsg}</p>}
          <div className="flex justify-end">
            <button
              onClick={handleSavePassword}
              disabled={!passwords.baru || !passwords.konfirmasi || passLoading}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                !passwords.baru || !passwords.konfirmasi || passLoading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {passLoading ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </div>
      </Section>
    </div>
  )
}
